'use client'

import { useState } from 'react'
import { Check, ChevronsUpDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'

/** Matches `admin-cases-view.tsx`'s existing `UNASSIGNED_VALUE` sentinel — shared here so every
 * caller (list-row picker and the inline client-detail control) agrees on the same value. */
export const ADVISOR_UNASSIGNED_VALUE = 'unassigned'

export interface AdvisorOption {
  id: string
  name: string | null
  email: string
}

/**
 * Searchable "Name — email" advisor picker (shadcn `Command` + `Popover`) — replaces a plain
 * `Select`, which has two problems as staff headcount grows: a long flat list with no search, and
 * no way to tell apart two staff members who happen to share a name (the trigger for this
 * component; see the 14-item polish pass, item 5). Email disambiguates because it's this app's
 * real staff identifier — guaranteed unique and present, unlike name.
 */
export function AdvisorCombobox({
  value,
  onValueChange,
  options,
  unassignedLabel = 'Unassigned',
  ariaLabel = 'Advisor',
  className,
}: {
  value: string
  onValueChange: (value: string) => void
  options: AdvisorOption[]
  unassignedLabel?: string
  /** `role="combobox"` doesn't support name-from-content per ARIA, so the trigger needs an
   * explicit accessible name — this is that name's prefix (e.g. "Advisor: Priya Staff"). */
  ariaLabel?: string
  className?: string
}) {
  const [open, setOpen] = useState(false)

  const selectedOption = options.find((o) => o.id === value)
  const selectedLabel =
    value === ADVISOR_UNASSIGNED_VALUE
      ? unassignedLabel
      : (selectedOption?.name ?? selectedOption?.email ?? unassignedLabel)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label={`${ariaLabel}: ${selectedLabel}`}
          className={cn('w-[150px] justify-between font-normal', className)}
        >
          <span className="truncate">{selectedLabel}</span>
          <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[320px] overflow-hidden p-0">
        <Command>
          <CommandInput placeholder="Search advisor…" />
          <CommandList>
            <CommandEmpty>No advisor found.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value={unassignedLabel}
                onSelect={() => {
                  onValueChange(ADVISOR_UNASSIGNED_VALUE)
                  setOpen(false)
                }}
              >
                <Check
                  className={cn(
                    'size-4',
                    value === ADVISOR_UNASSIGNED_VALUE ? 'opacity-100' : 'opacity-0',
                  )}
                />
                {unassignedLabel}
              </CommandItem>
              {options.map((option) => (
                <CommandItem
                  key={option.id}
                  value={`${option.name ?? ''} ${option.email}`}
                  onSelect={() => {
                    onValueChange(option.id)
                    setOpen(false)
                  }}
                >
                  <Check
                    className={cn(
                      'size-4 shrink-0',
                      value === option.id ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  <span className="min-w-0 flex-1 truncate">
                    {option.name ? `${option.name} — ${option.email}` : option.email}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
